import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { AddModeratorOutlined, DeleteOutline, Fingerprint, Key } from '@mui/icons-material';
import { webauthnService } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import SurfaceCard from '../components/ui/SurfaceCard';

type Credential = {
  id: string;
  deviceName: string | null;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
};

const SecurityPage: React.FC = () => {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const supported = webauthnService.isSupported();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCredentials(await webauthnService.listCredentials());
    } catch {
      setMessage({ type: 'error', text: 'Impossibile caricare le passkey.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRegister = async () => {
    setRegistering(true);
    setMessage(null);
    try {
      const defaultName = /iPhone|iPad/.test(navigator.userAgent) ? 'iPhone/iPad'
        : /Android/.test(navigator.userAgent) ? 'Android'
        : /Windows/.test(navigator.userAgent) ? 'Windows'
        : /Mac/.test(navigator.userAgent) ? 'Mac'
        : 'Questo dispositivo';
      await webauthnService.registerPasskey(defaultName);
      setMessage({ type: 'success', text: 'Passkey registrata con successo.' });
      await load();
    } catch (e: any) {
      const name = e?.name || '';
      if (name === 'NotAllowedError') {
        setMessage({ type: 'info', text: 'Registrazione annullata.' });
      } else {
        setMessage({ type: 'error', text: e?.response?.data?.error?.message || 'Registrazione passkey non riuscita.' });
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await webauthnService.deleteCredential(id);
      setCredentials((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setMessage({ type: 'error', text: 'Impossibile eliminare la passkey.' });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Sicurezza"
        subtitle="Gestisci le passkey per accedere senza password, anche da PC non tuoi tramite QR e telefono."
        actions={
          <Button
            variant="contained"
            startIcon={<AddModeratorOutlined />}
            onClick={handleRegister}
            disabled={!supported || registering}
          >
            {registering ? 'Registrazione…' : 'Aggiungi passkey'}
          </Button>
        }
      />

      {!supported && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Questo browser non supporta le passkey (WebAuthn). Usa un browser aggiornato.
        </Alert>
      )}
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <SurfaceCard sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
          <Fingerprint sx={{ color: 'var(--accent-blue)' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Le tue passkey
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Registra una passkey sul tuo telefono: potrai accedere con Face ID / impronta e usarla per
          approvare l'accesso da altri computer.
        </Typography>
        <Divider sx={{ mb: 1, borderColor: 'var(--border-color)' }} />

        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : credentials.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'var(--text-muted)' }}>
            <Key sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
            <Typography variant="body2">Nessuna passkey registrata.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {credentials.map((c) => (
              <ListItem
                key={c.id}
                sx={{ px: 0 }}
                secondaryAction={
                  <IconButton edge="end" aria-label="elimina" onClick={() => handleDelete(c.id)}>
                    <DeleteOutline />
                  </IconButton>
                }
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'var(--accent-blue)' }}>
                  <Key />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {c.deviceName || 'Passkey'}
                      </Typography>
                      {c.backedUp && <Chip label="Sincronizzata" size="small" color="success" variant="outlined" />}
                    </Box>
                  }
                  secondary={`Creata il ${new Date(c.createdAt).toLocaleDateString('it-IT')}${
                    c.lastUsedAt ? ` · Ultimo uso ${new Date(c.lastUsedAt).toLocaleDateString('it-IT')}` : ''
                  }`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </SurfaceCard>
    </Box>
  );
};

export default SecurityPage;
